/** @jsxImportSource @emotion/react */
import React, { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ITransaction } from '../../../../api/transactions/types';

import chargeFinishedStyles from './charge-finished.styles';
import { InfoPanel } from '../../components/info-panel/info-panel.component';
import chargePanelStyles from '../../components/info-panel/info-panel.styles';
import { selectAppState } from '../../../../store/app/app.selector';
import { formatDateToLocaleString, is24HourFormat } from '../../../../utils/utils';
import { useIntl } from 'react-intl';
import chargeProcessStyles from '../charge-process/charge-process.styles';
import { useNavigate } from 'react-router-dom';
import closeIcon from '../../../../assets/icons/close-icon.svg';

type PropTypes = {
    guest: ITransaction;
};

export const ChargeFinished: FC<PropTypes> = ({ guest }) => {
    const navigation = useNavigate();
    const intl = useIntl();
    const { transaction: charge } = guest;
    const { locale } = useSelector(selectAppState);

    const is24Hour = useMemo(() => is24HourFormat(), []);

    const closeHandler = () => {
        navigation(`/`);
    };

    return (
        <div css={chargeFinishedStyles.content}>
            <div css={chargeFinishedStyles.headerContainer}>
                <div css={chargeProcessStyles.businessCardInfoInnerContainer}>
                    <div css={chargeProcessStyles.businessCardInfoTitle}>
                        {guest?.__id
                            ? `${intl.formatMessage({ id: 'transaction' })} #${guest.__id}`
                            : intl.formatMessage({ id: 'startingTransaction' })}
                    </div>
                    {charge?.startAt && charge?.stopAt ? (
                        <div css={chargeProcessStyles.transactionDate}>
                            {formatDateToLocaleString(new Date(charge.startAt), locale)}
                            {' - '}
                            {formatDateToLocaleString(new Date(charge.stopAt), locale)}
                        </div>
                    ) : null}
                </div>
                <div css={chargeProcessStyles.arrowRight}>
                    <img
                        css={chargeProcessStyles.arrowRightIcon}
                        onClick={closeHandler}
                        src={closeIcon}
                        alt="arrow-right"
                    />
                </div>
            </div>
            <InfoPanel guest={guest} charge={charge} />
            <p css={chargeFinishedStyles.thanks}>
                {intl.formatMessage({
                    id: 'thanksForChargingWithUs',
                })}
            </p>
            <a
                href={`${process.env.REACT_APP_API_URL}/commercial/transactions/${guest._id}/receipt?locale=${locale}&is24Hour=${is24Hour}`}
                css={chargePanelStyles.downloadLink}
                target="_blank"
                rel="noreferrer"
                download>
                <span css={chargePanelStyles.downloadReceipt}>
                    {intl.formatMessage({ id: 'downloadReceipt' })}
                </span>
                <svg
                    width="25"
                    height="24"
                    viewBox="0 0 25 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M12.5 12L12.5 18"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M15.5 16L12.5 18L9.5 16"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M18.5 21H6.5C5.94772 21 5.5 20.5523 5.5 20L5.5 4C5.5 3.44772 5.94772 3 6.5 3L14.0631 3C14.3416 3 14.6076 3.11619 14.7968 3.32059L19.2338 8.11246C19.4049 8.29731 19.5 8.53995 19.5 8.79187L19.5 20C19.5 20.5523 19.0523 21 18.5 21Z"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M19.5 9L14.5 9C13.9477 9 13.5 8.55228 13.5 8L13.5 3"
                        stroke="black"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </a>
        </div>
    );
};
